"use client";

import * as React from "react";
import confetti from "canvas-confetti";
import Image from "next/image";
import { Mail, Phone, MessageSquare, ShieldAlert, Award, ArrowRight, RotateCcw, Clock, CheckCircle2, MapPin } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { CopyButton } from "@/components/ui/CopyButton";
import { FileUpload, UploadedFileItem } from "@/components/ui/FileUpload";
import { FormSuccess } from "@/components/ui/FormSuccess";
import { ErrorState } from "@/components/ui/ErrorState";
import { useAutosaveForm } from "@/hooks/useAutosaveForm";
import { globalToast } from "@/components/ui/ToastProvider";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal";

import { getStoredAttribution } from "@/components/analytics/UTMTracker";

export default function ContactContent() {
  const initialForm = {
    name: "",
    email: "",
    phone: "",
    company: "",
    projectType: "build",
    budget: "mid",
    message: "",
  };

  const {
    formData,
    setFormData,
    lastSaved,
    hasDraft,
    clearDraft,
    markSubmitted,
  } = useAutosaveForm({
    formKey: "contact_inquiry",
    initialValues: initialForm,
  });

  const [attachedFile, setAttachedFile] = React.useState<UploadedFileItem | null>(null);
  const [currency, setCurrency] = React.useState<"USD" | "INR">("USD");
  const [status, setStatus] = React.useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedbackMsg, setFeedbackMsg] = React.useState("");
  const [submittedRef, setSubmittedRef] = React.useState("");
  const [showClearModal, setShowClearModal] = React.useState(false);

  const projectTypes = [
    { label: "Build (Web/Apps)", value: "build" },
    { label: "Automate (CRM/AI)", value: "automate" },
    { label: "Scale (Cloud/DevOps)", value: "scale" },
    { label: "Grow (SEO/Consulting)", value: "grow" },
  ];

  const budgetRanges = currency === "INR" ? [
    { label: "Below ₹1 Lakh", value: "low", display: "Below ₹1 Lakh", dealVal: 80000 },
    { label: "₹1 Lakh - ₹5 Lakhs", value: "mid", display: "₹1 Lakh – ₹5 Lakhs", dealVal: 300000 },
    { label: "₹5 Lakhs - ₹15 Lakhs", value: "high", display: "₹5 Lakhs – ₹15 Lakhs", dealVal: 800000 },
    { label: "₹15 Lakhs+ (Enterprise)", value: "custom", display: "Enterprise Scope (₹15L+)", dealVal: 1800000 },
  ] : [
    { label: "Under $5,000", value: "low", display: "Under $5,000", dealVal: 4000 },
    { label: "$5,000 - $15,000", value: "mid", display: "$5,000 – $15,000", dealVal: 10000 },
    { label: "$15,000 - $50,000", value: "high", display: "$15,000 – $50,000", dealVal: 25000 },
    { label: "$50,000+ (Enterprise)", value: "custom", display: "Enterprise Scope ($50k+)", dealVal: 60000 },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setFeedbackMsg("");

    try {
      const attribution = getStoredAttribution();

      const serviceMap: Record<string, string> = {
        build: "Custom Software Development",
        automate: "AI Automation & Agents",
        scale: "Cloud & DevOps Infrastructure",
        grow: "Technology Consulting",
      };

      const selectedBudgetObj = budgetRanges.find((b) => b.value === formData.budget) || budgetRanges[1];

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        company: formData.company || undefined,
        service_type: serviceMap[formData.projectType] || "Custom Software Development",
        budget_range: selectedBudgetObj.display,
        currency,
        estimated_deal_value: selectedBudgetObj.dealVal,
        project_details: formData.message,
        landing_page: "/contact",
        ...attribution,
      };

      const response = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus("success");
        setFeedbackMsg(data.message);
        
        // Trigger premium Confetti explosion
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
        });

        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          projectType: "build",
          budget: "mid",
          message: "",
        });
        setAttachedFile(null);
        setSubmittedRef(data.leadId ? `REF-${data.leadId.slice(0, 8).toUpperCase()}` : `REF-${Date.now().toString().slice(-6)}`);
        markSubmitted();

        globalToast.success("Scoping inquiry received! Our technical director will respond within 24 hours.");

        // Smooth redirect to dedicated conversion page for Google Ads & Analytics tracking
        setTimeout(() => {
          window.location.href = "/thank-you";
        }, 1200);
      } else {
        setStatus("error");
        setFeedbackMsg(data.error || "Form submission failed.");
      }
    } catch (err) {
      console.error(err);
      setStatus("error");
      setFeedbackMsg("Network error. Please try again later.");
    }
  };


  return (
    <div className="w-full flex-grow py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden">
      {/* Background Mesh */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="bg-mesh" />
      </div>

      <div className="mx-auto max-w-7xl relative z-10 space-y-12">
        {/* Architectural Hero Header */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/20 text-[#2563EB] text-xs font-mono">
            <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse" />
            EXECUTIVE CONSULTATION // DIRECT PRINCIPAL ACCESS
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-foreground tracking-tight leading-[1.08]">
                Technical Scoping &amp; <br />
                <span className="text-[#2563EB]">Architectural Consultation.</span>
              </h1>
              <p className="text-base sm:text-lg text-text-muted mt-3 max-w-2xl">
                Direct engagement with principal software architects. Zero sales fluff, guaranteed NDA protection, and deterministic timelines.
              </p>
            </div>

            <div className="flex items-center gap-4 text-xs font-mono text-text-muted shrink-0 pb-1">
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#2563EB]" /> &lt;24h Turnaround</span>
              <span className="text-foreground/20">•</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> NDA Assured</span>
            </div>
          </div>

          {/* Architectural Office Banner */}
          <div className="relative rounded-2xl overflow-hidden border border-[#111318]/10 h-64 sm:h-80 lg:h-96 shadow-sm">
            <Image
              src="/images/storytelling/office/meeting-room.webp"
              alt="Johnson Softwares Executive Scoping Room"
              fill
              priority
              className="object-cover object-center"
              sizes="(max-width: 1280px) 100vw, 1280px"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111318]/80 via-[#111318]/20 to-transparent" />
            
            <div className="absolute bottom-6 left-6 right-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4 text-white">
              <div>
                <span className="px-2.5 py-1 rounded bg-white/20 backdrop-blur-md text-[11px] font-mono tracking-wider uppercase">
                  CONFIDENTIAL SCOPING ROOM // NDA PROTECTED
                </span>
                <p className="text-lg sm:text-xl font-bold mt-2 font-heading">
                  Architecture &amp; Commercial Discovery Session
                </p>
                <p className="text-xs text-white/80 max-w-md mt-0.5">
                  Discuss database topology, bespoke CRM flows, cloud economics, or AI automations directly with leadership.
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 bg-white/10 backdrop-blur-md px-3.5 py-2 rounded-xl border border-white/15 text-xs font-mono">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                DIRECT ACCESS GUARANTEED
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Direct channels and trust assurances */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-[#2563EB] font-bold">
                Direct Communication Channels
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-extrabold text-foreground mt-2 leading-tight">
                Immediate Engineer Access
              </h2>
              <p className="text-sm text-text-muted mt-2">
                Pick your preferred communication channel below. Principal architects review every incoming scope within 24 hours.
              </p>
            </div>

            {/* Direct Cards */}
            <div className="flex flex-col gap-4">
              {/* WhatsApp card */}
              <a
                href="https://wa.me/919880893416"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-5 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl hover:bg-emerald-500/15 transition-all group cursor-pointer"
              >
                <div className="flex gap-4 items-center">
                  <span className="p-3 bg-emerald-600 text-white rounded-xl shadow-xs">
                    <MessageSquare className="h-5 w-5" />
                  </span>
                  <div>
                    <span className="block font-bold text-foreground text-sm font-heading">Instant WhatsApp Chat</span>
                    <span className="block text-xs text-text-muted">Direct line to senior technical architect.</span>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-emerald-600 group-hover:translate-x-1 transition-transform" />
              </a>

              {/* Email Copier */}
              <div className="flex items-center justify-between p-5 bg-white dark:bg-card border border-[#111318]/10 rounded-2xl shadow-xs">
                <div className="flex gap-4 items-center min-w-0">
                  <span className="p-3 bg-[#2563EB]/10 rounded-xl text-[#2563EB] shrink-0">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <span className="block font-bold text-foreground text-sm font-heading">Direct RFP &amp; Inquiries</span>
                    <span className="block text-xs text-text-muted truncate font-mono">contact@johnsonsoftwares.com</span>
                  </div>
                </div>
                <CopyButton text="contact@johnsonsoftwares.com" label="Copy" variant="pill" />
              </div>

              {/* Direct call */}
              <div className="flex items-center justify-between p-5 bg-white dark:bg-card border border-[#111318]/10 rounded-2xl shadow-xs">
                <a href="tel:+919880893416" className="flex gap-4 items-center flex-1 hover:opacity-80 transition-opacity">
                  <span className="p-3 bg-[#2563EB]/10 rounded-xl text-[#2563EB] shrink-0">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <span className="block font-bold text-foreground text-sm font-heading">Direct Phone Line</span>
                    <span className="block text-xs text-text-muted font-mono">+91 98808 93416</span>
                  </div>
                </a>
                <CopyButton text="+919880893416" label="Call" variant="pill" />
              </div>

              {/* Physical Office Address */}
              <div className="flex items-center justify-between p-5 bg-white dark:bg-card border border-[#111318]/10 rounded-2xl shadow-xs">
                <div className="flex gap-4 items-center min-w-0">
                  <span className="p-3 bg-[#2563EB]/10 rounded-xl text-[#2563EB] shrink-0">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <span className="block font-bold text-foreground text-sm font-heading">Bengaluru Engineering Office</span>
                    <span className="block text-xs text-text-muted leading-relaxed">
                      27th Main Road, HSR Layout, Bangalore 560102, Karnataka, India
                    </span>
                  </div>
                </div>
                <CopyButton text="27th Main Road, HSR Layout, Bangalore 560102, Karnataka, India" label="Copy" variant="pill" />
              </div>
            </div>

            {/* Trust Assurances block */}
            <div className="p-6 bg-white/70 dark:bg-card/70 rounded-2xl border border-[#111318]/10 space-y-4 shadow-xs">
              <div className="flex gap-3 items-start">
                <ShieldAlert className="h-5 w-5 text-[#2563EB] shrink-0" />
                <p className="text-xs text-text-muted leading-relaxed">
                  <strong>NDA Assured</strong>: We sign Non-Disclosure Agreements before starting scoping sessions. Your operational models are fully protected.
                </p>
              </div>
              <div className="flex gap-3 items-start">
                <Award className="h-5 w-5 text-primary dark:text-accent shrink-0" />
                <p className="text-xs text-text-muted leading-relaxed">
                  <strong>Flat Estimates</strong>: All project templates contain clear pricing maps to guarantee transparent scoping without pricing creep.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Scoping Consultation Form */}
          <div className="lg:col-span-7">
            <Card className="p-6 sm:p-10" glowColor="rgba(37, 99, 235, 0.15)">
              <h2 className="font-heading font-extrabold text-2xl text-foreground mb-2">
                Request a Scoping Audit
              </h2>
              <p className="text-xs text-text-muted mb-8 leading-relaxed">
                Outline your requirements below to schedule a call with our technical lead.
              </p>

              {status === "success" ? (
                <FormSuccess
                  referenceId={submittedRef}
                  onReset={() => {
                    setStatus("idle");
                    clearDraft();
                  }}
                />
              ) : status === "error" ? (
                <ErrorState
                  message={feedbackMsg}
                  onRetry={() => setStatus("idle")}
                />
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Autosave Draft Indicator Banner */}
                  {hasDraft && lastSaved && (
                    <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 text-xs">
                      <span className="text-blue-700 dark:text-blue-300 flex items-center gap-1.5 font-medium">
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        Draft restored ({lastSaved.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowClearModal(true)}
                        className="text-[11px] text-slate-500 hover:text-rose-500 hover:underline cursor-pointer flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" />
                        <span>Clear draft</span>
                      </button>
                    </div>
                  )}

                  {/* Name & Email inputs */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-foreground uppercase tracking-wider">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-border-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-foreground uppercase tracking-wider">
                        Business Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-border-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground"
                      />
                    </div>
                  </div>

                  {/* Phone & Company Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-foreground uppercase tracking-wider">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        placeholder="+91 98765 43210 or +1..."
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-border-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-foreground uppercase tracking-wider">
                        Company Name
                      </label>
                      <input
                        type="text"
                        placeholder="Acme Corp"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-border-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground"
                      />
                    </div>
                  </div>

                  {/* Project Type Select */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-foreground uppercase tracking-wider mb-1">
                      Project Goal *
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {projectTypes.map((type) => {
                        const isSelected = formData.projectType === type.value;
                        return (
                          <button
                            type="button"
                            key={type.value}
                            onClick={() => setFormData({ ...formData, projectType: type.value })}
                            className={`px-4 py-2.5 rounded-lg border text-xs font-semibold text-center transition-all cursor-pointer ${
                              isSelected
                                ? "bg-primary border-primary text-white shadow-sm"
                                : "bg-slate-50 dark:bg-slate-900 border-border-muted/80 text-text-muted hover:text-foreground"
                            }`}
                          >
                            {type.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Budget Ranges Select */}
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-bold text-foreground uppercase tracking-wider">
                        Project Budget *
                      </label>
                      <div className="inline-flex p-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 border border-border-muted/60 text-[11px] font-bold shadow-xs">
                        <button
                          type="button"
                          onClick={() => setCurrency("USD")}
                          className={`px-2.5 py-0.5 rounded-md transition-all ${
                            currency === "USD"
                              ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                              : "text-text-muted hover:text-foreground"
                          }`}
                        >
                          🇺🇸 USD ($)
                        </button>
                        <button
                          type="button"
                          onClick={() => setCurrency("INR")}
                          className={`px-2.5 py-0.5 rounded-md transition-all ${
                            currency === "INR"
                              ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                              : "text-text-muted hover:text-foreground"
                          }`}
                        >
                          🇮🇳 INR (₹)
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {budgetRanges.map((budget) => {
                        const isSelected = formData.budget === budget.value;
                        return (
                          <button
                            type="button"
                            key={budget.value}
                            onClick={() => setFormData({ ...formData, budget: budget.value })}
                            className={`px-4 py-2.5 rounded-lg border text-xs font-semibold text-center transition-all cursor-pointer ${
                              isSelected
                                ? "bg-primary border-primary text-white shadow-sm"
                                : "bg-slate-50 dark:bg-slate-900 border-border-muted/80 text-text-muted hover:text-foreground"
                            }`}
                          >
                            {budget.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Message Detail */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-foreground uppercase tracking-wider">
                      Operational Requirements / Message *
                    </label>
                    <textarea
                      rows={4}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Outline details such as legacy systems to replace, user load, and required APIs."
                      className="px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-border-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground resize-none"
                    />
                  </div>

                  {/* 5. Drag-Drop File Upload */}
                  <FileUpload
                    onFileSelect={(file) => setAttachedFile(file)}
                    label="Attach Project Specs / RFP / Wireframes (Optional)"
                    hint="PDF, DOCX, ZIP, or PNG/JPG up to 15MB"
                  />

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="gradient"
                    className="w-full justify-center"
                    isLoading={status === "submitting"}
                  >
                    Submit Audit Request
                  </Button>
                </form>
              )}
            </Card>
          </div>

        </div>
      </div>

      {/* Draft Reset Confirmation Modal */}
      <ConfirmationModal
        isOpen={showClearModal}
        onClose={() => setShowClearModal(false)}
        onConfirm={() => {
          const prev = { ...formData };
          clearDraft();
          setShowClearModal(false);
          globalToast.info("Form draft cleared", {
            undo: () => {
              setFormData(prev);
              globalToast.success("Form draft restored");
            },
            undoLabel: "Undo",
          });
        }}
        title="Clear Saved Draft?"
        description="Are you sure you want to discard your saved draft? All filled inputs will be reset to blank."
        confirmLabel="Discard Draft"
        cancelLabel="Keep Draft"
        variant="danger"
      />
    </div>
  );
}
